import { Controller } from '@hotwired/stimulus'
import bootstrap from 'bootstrap/dist/js/bootstrap'

const METERS_PER_MILE = 1609.344
const SQ_METERS_PER_SQ_MILE = 2589988.11
// A saved radius is a ring of this many points; DeliveryBoundary reads 32 or more
// equidistant points back as a radius when the page loads.
const RING_POINTS = 64
// A circle turned into a custom shape: few enough corners to drag by hand.
const SEED_POINTS = 16
const DEFAULT_MILES = 3
const MIN_MILES = 0.5
const HINTS = {
  edit: 'Drag a corner to move it, drag a midpoint to add a corner, click a corner to remove it.',
  draw: 'Click the map to add corners, then click the first corner or Finish shape.'
}
// A light base map that still reads at a glance: white roads with clear edges, soft amber
// highways, blue water, green parks, street names on. Businesses and transit stay off.
const MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#eef1f4' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#3f4851' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 3 }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#aab4be' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#212529' }] },
  { featureType: 'administrative.neighborhood', elementType: 'labels.text.fill', stylers: [{ color: '#6c757d' }] },
  { featureType: 'landscape.man_made', elementType: 'geometry', stylers: [{ color: '#e6eaee' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', stylers: [{ visibility: 'on' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#d3e6cc' }] },
  { featureType: 'poi.park', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#cdd4db' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#56606a' }] },
  { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#fdefc8' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#e8c979' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#a9cde3' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#4f7a94' }] }
]
// A map pin, 36×48: a ground shadow, the teardrop (tip at 18,44) and a white disc for its mark.
const PIN_SHAPE = 'M18 3C10.5 3 4.5 8.9 4.5 16.2c0 9.3 11.2 21.7 12.7 23.3a1.1 1.1 0 0 0 1.6 0c1.5-1.6 12.7-14 12.7-23.3C31.5 8.9 25.5 3 18 3z'
// A storefront (scalloped awning over a shopfront with a door), drawn inside the disc.
const STORE_MARK = (color) => `
  <path d="M12.6 11.3h10.8l1.1 3a1.8 1.8 0 0 1-3.5.6 1.8 1.8 0 0 1-3.5 0 1.8 1.8 0 0 1-3.5 0 1.8 1.8 0 0 1-3.5-.6z" fill="${color}"/>
  <path d="M13.7 16.3v4.3h8.6v-4.3" fill="none" stroke="${color}" stroke-width="1.4" stroke-linejoin="round"/>
  <rect x="16.9" y="17.6" width="2.2" height="3" rx=".4" fill="${color}"/>`
const DOT_MARK = (color) => `<circle cx="18" cy="16" r="3.4" fill="${color}"/>`

// The delivery area on the Delivery settings page: a radius around the restaurant, or a
// shape drawn by hand. Either way the area is sent as one ring of [lat, lng] points in
// the settings form's hidden boundary_path field, and only when it was changed.
export default class extends Controller {
  static targets = [
    'canvas', 'field', 'modeTab', 'preset', 'slider', 'output', 'status', 'dirty', 'search', 'result',
    'radiusTools', 'customTools', 'editButtons', 'drawButtons', 'undo', 'reset', 'finish', 'hint', 'fullscreen'
  ]

  // path/radius: the saved area. postedPath/postedRadius: an area sent with a save that failed on
  // something else (a wrong day of hours), shown again so it isn't lost.
  // max: the farthest a delivery area may reach, in miles (DeliveryBoundary::MAX_REACH_MILES).
  static values = {
    center: Object, path: Array, radius: Number, postedPath: Array, postedRadius: Number, max: Number
  }

  connect() {
    if (!window.google?.maps?.geometry) {
      this.statusTarget.textContent = "The map didn't load. Refresh the page to try again."
      return
    }

    this.center = new google.maps.LatLng(this.centerValue.lat, this.centerValue.lng)
    const root = getComputedStyle(document.documentElement)
    this.color = root.getPropertyValue('--bs-primary').trim() || '#a82402'
    this.dangerColor = root.getPropertyValue('--bs-danger').trim() || '#dc3545'
    const successColor = root.getPropertyValue('--bs-success').trim() || '#198754'
    this.testIcons = {
      in: this.#pinIcon(successColor, DOT_MARK(successColor)),
      out: this.#pinIcon(this.dangerColor, DOT_MARK(this.dangerColor))
    }
    this.savedKey = this.#key(this.pathValue.map(([lat, lng]) => new google.maps.LatLng(lat, lng)))

    // A corner drag fires set_at on every move; one gesture is one undo step.
    this.releaseSnapshot = () => { this.snapshotTaken = false }
    document.addEventListener('pointerup', this.releaseSnapshot)

    this.canvasTarget.replaceChildren()
    this.map = new google.maps.Map(this.canvasTarget, {
      center: this.center,
      zoom: 12,
      styles: MAP_STYLES,
      clickableIcons: false,
      // The page draws its own zoom and full-screen buttons (.dz-map__controls).
      disableDefaultUI: true,
      // The map sits in a long settings page: a plain scroll moves the page, not the map.
      gestureHandling: 'cooperative'
    })
    this.pin = new google.maps.Marker({
      map: this.map,
      position: this.center,
      title: 'Your restaurant',
      // A zone can have a corner on the restaurant itself; the pin must not take its drag.
      clickable: false,
      zIndex: 3,
      icon: this.#pinIcon(this.color, STORE_MARK(this.color))
    })
    // The reach, drawn: a dashed line out from the restaurant with its miles at the middle.
    this.measureLine = new google.maps.Polyline({ map: this.map, clickable: false, strokeOpacity: 0, zIndex: 1 })
    this.measureLabel = new google.maps.Marker({
      map: this.map,
      clickable: false,
      zIndex: 2,
      icon: { path: google.maps.SymbolPath.CIRCLE, scale: 0 }
    })
    this.#bindSearch()
    this.#bindFullscreen()
    if (this.postedPathValue.length >= 3) {
      this.#show(this.postedPathValue, this.postedRadiusValue)
    } else {
      this.#restoreSaved()
    }
  }

  disconnect() {
    clearTimeout(this.fitTimer)
    document.removeEventListener('pointerup', this.releaseSnapshot)
    document.removeEventListener('fullscreenchange', this.onFullscreen)
    ;[this.map, this.circle, this.polygon, this.autocomplete].forEach((object) => {
      if (object) google.maps.event.clearInstanceListeners(object)
    })
    // Places adds its suggestion list to <body> and never removes it.
    document.querySelectorAll('.pac-container').forEach((list) => list.remove())
  }

  pickMode(event) {
    this.cancelDraw()
    this.mode = event.currentTarget.dataset.mode
    this.#render()
    this.#fit()
  }

  pickPreset(event) {
    this.#setMiles(Number(event.currentTarget.dataset.miles))
    this.#fit()
  }

  slide() {
    this.#setMiles(Number(this.sliderTarget.value))
    clearTimeout(this.fitTimer)
    this.fitTimer = setTimeout(() => this.#fit(), 300)
  }

  undo() {
    const previous = this.history.pop()
    if (!previous) return

    this.customPath = previous
    this.#drawPolygon(previous)
    this.#sync()
  }

  reset() {
    this.cancelDraw()
    this.#restoreSaved()
  }

  startDraw() {
    this.drawing = true
    this.drawPoints = []
    this.polygon?.setMap(null)
    this.drawLine = new google.maps.Polyline({
      map: this.map, clickable: false, strokeColor: this.color, strokeOpacity: 0.9, strokeWeight: 2
    })
    this.map.setOptions({ draggableCursor: 'crosshair', disableDoubleClickZoom: true })
    this.drawListeners = [
      this.map.addListener('click', (event) => this.#addDrawPoint(event.latLng)),
      this.map.addListener('mousemove', (event) => this.drawLine.setPath([...this.drawPoints, event.latLng]))
    ]
    this.#sync()
  }

  finishDraw() {
    if (!this.drawing || this.drawPoints.length < 3) return

    this.history.push(this.customPath)
    this.customPath = this.drawPoints
    this.#stopDrawing()
    this.#drawPolygon(this.customPath)
    this.#sync()
    this.#fit()
  }

  cancelDraw() {
    if (!this.drawing) return

    this.#stopDrawing()
    this.#drawPolygon(this.customPath)
    this.#sync()
  }

  zoomIn() {
    this.map?.setZoom(this.map.getZoom() + 1)
  }

  zoomOut() {
    this.map?.setZoom(this.map.getZoom() - 1)
  }

  // The whole frame goes full screen, so the buttons and the address verdict come along.
  toggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      this.canvasTarget.closest('.dz-map').requestFullscreen()
    }
  }

  // Enter picks a suggestion; it must not submit the settings form around the map.
  holdEnter(event) {
    event.preventDefault()
  }

  // Delivery was switched on: the map was laid out at 0×0 inside the closed section.
  refit(event) {
    if (this.map && event.target.contains(this.element)) this.#fit()
  }

  #restoreSaved() {
    this.#show(this.pathValue, this.radiusValue)
  }

  #show(path, radius) {
    const points = path.map(([lat, lng]) => new google.maps.LatLng(lat, lng))

    this.mode = radius > 0 || points.length < 3 ? 'radius' : 'custom'
    this.miles = radius > 0 ? radius : DEFAULT_MILES
    this.customPath = this.mode === 'custom' ? points : null
    this.history = []
    this.#render()
    this.#fit()
  }

  #render() {
    const radius = this.mode === 'radius'
    this.modeTabTargets.forEach((tab) => {
      const on = tab.dataset.mode === this.mode
      tab.classList.toggle('active', on)
      tab.setAttribute('aria-pressed', on)
    })
    this.radiusToolsTarget.hidden = !radius
    this.customToolsTarget.hidden = radius

    if (radius) {
      this.polygon?.setMap(null)
      this.#drawCircle()
    } else {
      this.circle?.setMap(null)
      this.customPath ||= this.#ring(this.miles, SEED_POINTS)
      this.#drawPolygon(this.customPath)
    }
    this.#sync()
  }

  #setMiles(miles) {
    this.miles = this.#clampMiles(miles)
    this.#drawCircle()
    this.#sync()
  }

  #clampMiles(miles) {
    return Math.min(this.maxValue, this.#roundMiles(miles))
  }

  #roundMiles(miles) {
    return Math.max(MIN_MILES, this.#tenths(miles))
  }

  #drawCircle() {
    if (!this.circle) {
      this.circle = new google.maps.Circle({ ...this.#areaStyle(), editable: true, draggable: false })
      // The center handle can still be dragged; the radius is always around the restaurant.
      this.circle.addListener('center_changed', () => {
        if (!this.circle.getCenter().equals(this.center)) this.circle.setCenter(this.center)
      })
      this.circle.addListener('radius_changed', () => {
        if (this.settingRadius) return

        // Past the limit is allowed here: the circle turns red and says so, and Save refuses it.
        this.miles = this.#roundMiles(this.circle.getRadius() / METERS_PER_MILE)
        this.#sync()
      })
      this.circle.addListener('mouseup', () => this.#drawCircle())
    }
    this.settingRadius = true
    this.circle.setOptions({ map: this.map, center: this.center, radius: this.miles * METERS_PER_MILE })
    this.settingRadius = false
  }

  #drawPolygon(path) {
    this.polygon?.setMap(null)
    this.polygon = new google.maps.Polygon({ ...this.#areaStyle(), paths: path, editable: true })

    const corners = this.polygon.getPath()
    ;['set_at', 'insert_at', 'remove_at'].forEach((change) => {
      corners.addListener(change, () => {
        if (!this.snapshotTaken) {
          this.history.push(this.customPath)
          this.snapshotTaken = true
        }
        this.customPath = corners.getArray().slice()
        this.#sync()
      })
    })
    this.polygon.addListener('click', (event) => {
      if (event.vertex != null && corners.getLength() > 3) corners.removeAt(event.vertex)
    })
  }

  #addDrawPoint(latLng) {
    const first = this.drawPoints[0]
    if (first && this.drawPoints.length >= 3 && this.#nearOnScreen(first, latLng)) {
      this.finishDraw()
      return
    }

    this.drawPoints.push(latLng)
    this.drawLine.setPath(this.drawPoints)
    this.finishTarget.disabled = this.drawPoints.length < 3
  }

  // Within about 12 pixels at the current zoom.
  #nearOnScreen(a, b) {
    const metersPerPixel = 156543.03392 * Math.cos(this.center.lat() * Math.PI / 180) / 2 ** this.map.getZoom()
    return google.maps.geometry.spherical.computeDistanceBetween(a, b) < metersPerPixel * 12
  }

  #stopDrawing() {
    this.drawing = false
    this.drawListeners?.forEach((listener) => listener.remove())
    this.drawLine?.setMap(null)
    this.map.setOptions({ draggableCursor: null, disableDoubleClickZoom: false })
  }

  // Hidden where the browser can't go full screen (iPhone Safari).
  #bindFullscreen() {
    this.fullscreenTarget.hidden = !document.fullscreenEnabled
    this.onFullscreen = () => {
      const full = Boolean(document.fullscreenElement)
      // Full screen has no page to scroll, so a plain scroll or one finger moves the map.
      this.map.setOptions({ gestureHandling: full ? 'greedy' : 'cooperative' })
      const label = full ? 'Exit full screen' : 'Full screen'
      this.fullscreenTarget.setAttribute('aria-label', label)
      // The button's tooltip comes from ajax-bs-elements; same bootstrap module, same instance.
      bootstrap.Tooltip.getInstance(this.fullscreenTarget)?.setContent({ '.tooltip-inner': label })
      this.fullscreenTarget.querySelector('.bi').className = `bi ${full ? 'bi-fullscreen-exit' : 'bi-arrows-fullscreen'}`
    }
    document.addEventListener('fullscreenchange', this.onFullscreen)
  }

  #bindSearch() {
    if (!google.maps.places) {
      this.searchTarget.closest('.dz-map__search').hidden = true
      return
    }

    this.autocomplete = new google.maps.places.Autocomplete(this.searchTarget, { fields: ['geometry'] })
    this.autocomplete.bindTo('bounds', this.map)
    this.autocomplete.addListener('place_changed', () => {
      const place = this.autocomplete.getPlace()
      if (!place.geometry) return

      this.tested = place.geometry.location
      this.testPin ||= new google.maps.Marker({ map: this.map, zIndex: 4, title: 'Tested address', clickable: false })
      this.testPin.setPosition(this.tested)
      this.#showTestResult()
      this.#fit()
    })
  }

  // The verdict is for the area on screen, so it follows every change to it.
  #showTestResult() {
    if (!this.tested) return

    const ring = this.#currentRing()
    const inside = ring.length >= 3 &&
      google.maps.geometry.poly.containsLocation(this.tested, new google.maps.Polygon({ paths: ring }))
    const miles = google.maps.geometry.spherical.computeDistanceBetween(this.center, this.tested) / METERS_PER_MILE

    this.testPin.setIcon(inside ? this.testIcons.in : this.testIcons.out)
    this.resultTarget.hidden = false
    this.resultTarget.classList.toggle('dz-result--in', inside)
    this.resultTarget.classList.toggle('dz-result--out', !inside)
    this.resultTarget.textContent = `${inside ? 'Inside' : 'Outside'} your delivery area, ${this.#miles(miles)} mi away`
  }

  #sync() {
    const radius = this.mode === 'radius'
    const drawing = Boolean(this.drawing)

    this.presetTargets.forEach((preset) => {
      const on = Number(preset.dataset.miles) === this.miles
      preset.classList.toggle('active', on)
      preset.setAttribute('aria-pressed', on)
    })
    this.sliderTarget.value = this.miles
    this.sliderTarget.dispatchEvent(new Event('change', { bubbles: true }))
    this.outputTarget.textContent = `${this.#miles(this.miles)} mi`

    this.editButtonsTarget.hidden = drawing
    this.drawButtonsTarget.hidden = !drawing
    this.finishTarget.disabled = !drawing || this.drawPoints.length < 3
    this.undoTarget.disabled = this.history.length === 0
    this.hintTarget.textContent = drawing ? HINTS.draw : HINTS.edit

    const ring = drawing ? [] : this.#currentRing()
    const dirty = !drawing && this.#key(ring) !== this.savedKey
    this.resetTarget.disabled = !dirty && this.history.length === 0
    this.dirtyTarget.hidden = !dirty
    this.fieldTarget.value = dirty ? JSON.stringify(ring.map((point) => [this.#round(point.lat()), this.#round(point.lng())])) : ''

    // Rounded as the server rounds it, so the map and the save agree on what is too far.
    const far = this.#farthest(ring)
    const over = Boolean(far) && this.#tenths(far.miles) > this.maxValue
    const color = over ? this.dangerColor : this.color
    ;[this.circle, this.polygon].forEach((shape) => shape?.setOptions({ strokeColor: color, fillColor: color }))
    this.#measure(radius ? this.#radiusMeasure() : far, color)
    this.statusTarget.classList.toggle('dz-caption--over', over)
    this.statusTarget.textContent = drawing ? '' : this.#describe(ring, radius, far, over)
    this.#showTestResult()
  }

  #describe(ring, radius, far, over) {
    if (!far) return ''

    const area = google.maps.geometry.spherical.computeArea(ring) / SQ_METERS_PER_SQ_MILE
    const size = `about ${area < 10 ? area.toFixed(1) : Math.round(area)} sq mi`
    const text = radius
      ? `${this.#miles(this.miles)} mi radius, ${size}`
      : `Custom area, ${size}, reaches ${this.#miles(far.miles)} mi out`
    if (!over) return text

    // A saved area can predate the limit; only a changed one is refused on save.
    return `${text}. A delivery area can reach at most ${this.#miles(this.maxValue)} mi from your restaurant.`
  }

  // The ring's point farthest from the restaurant, and how far that is.
  #farthest(ring) {
    let far = null
    ring.forEach((point) => {
      const miles = google.maps.geometry.spherical.computeDistanceBetween(this.center, point) / METERS_PER_MILE
      if (!far || miles > far.miles) far = { point, miles }
    })
    return far
  }

  // A radius is measured toward the north-east, clear of the circle's four edge handles.
  #radiusMeasure() {
    return { point: google.maps.geometry.spherical.computeOffset(this.center, this.miles * METERS_PER_MILE, 45), miles: this.miles }
  }

  #measure(reach, color) {
    const shown = Boolean(reach) && !this.drawing
    this.measureLine.setVisible(shown)
    this.measureLabel.setVisible(shown)
    if (!shown) return

    this.measureLine.setOptions({
      path: [this.center, reach.point],
      icons: [{ icon: { path: 'M 0,-1 0,1', strokeColor: color, strokeOpacity: 0.9, strokeWeight: 2, scale: 2.5 }, offset: '0', repeat: '9px' }]
    })
    this.measureLabel.setPosition(google.maps.geometry.spherical.interpolate(this.center, reach.point, 0.5))
    this.measureLabel.setLabel({
      text: `${this.#miles(reach.miles)} mi`, className: 'dz-measure', color, fontSize: '12px', fontWeight: '700'
    })
  }

  #pinIcon(color, mark) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48">
      <defs><filter id="s" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="1.2"/></filter></defs>
      <ellipse cx="18" cy="44.5" rx="6.5" ry="2" fill="#000" opacity=".28" filter="url(#s)"/>
      <path d="${PIN_SHAPE}" fill="${color}" stroke="#fff" stroke-width="2"/>
      <circle cx="18" cy="16" r="8.4" fill="#fff"/>${mark}</svg>`
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(36, 48),
      anchor: new google.maps.Point(18, 44)
    }
  }

  #currentRing() {
    if (this.mode === 'radius') return this.#ring(this.miles, RING_POINTS)

    return this.polygon ? this.polygon.getPath().getArray().slice() : []
  }

  #ring(miles, count) {
    return Array.from({ length: count }, (_, i) =>
      google.maps.geometry.spherical.computeOffset(this.center, miles * METERS_PER_MILE, i * 360 / count))
  }

  #fit() {
    const ring = this.drawing ? this.drawPoints : this.#currentRing()
    if (ring.length === 0) return

    const bounds = new google.maps.LatLngBounds()
    ring.forEach((point) => bounds.extend(point))
    bounds.extend(this.center)
    if (this.tested) bounds.extend(this.tested)
    this.map.fitBounds(bounds, 32)
  }

  #areaStyle() {
    return {
      map: this.map, strokeColor: this.color, strokeOpacity: 1, strokeWeight: 2.5, fillColor: this.color, fillOpacity: 0.1
    }
  }

  #key(ring) {
    return ring.map((point) => `${point.lat().toFixed(6)},${point.lng().toFixed(6)}`).join(';')
  }

  #round(value) {
    return Math.round(value * 1e6) / 1e6
  }

  #tenths(value) {
    return Math.round(value * 10) / 10
  }

  #miles(value) {
    const tenths = this.#tenths(value)
    return Number.isInteger(tenths) ? String(tenths) : tenths.toFixed(1)
  }
}
